// This is the keycode for the mouse button that CAUSES the mouse event
// To be used with mouseEvent.button, because mouseEvent.which is deprecated
// https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button
// Usually Primary = left, Auxillary = middle wheel click, Secondary = right
// but this can be edited at the OS level by users.
export enum MouseButton {
  PRIMARY = 0,
  AUXILLARY = 1,
  SECONDARY = 2,
}

// This is a bitwise representation of all mouse buttons currenty being held,
// regardless if they were the one to trigger the mouse event from occuring
// https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/buttons
export enum ButtonMask {
  NONE = 0,
  PRIMARY = 1,
  SECONDARY = 2,
  AUXILLARY = 4,
  FOURTH = 8,
  FIFTH = 16,
}

export function wasPrimaryMouseClick(event: MouseEvent): boolean {
  return event.button === MouseButton.PRIMARY && event.buttons === ButtonMask.PRIMARY;
}

/** True if the primary mouse button was released. */
export function wasPrimaryMouseUp(event: MouseEvent): boolean {
  return event.button === MouseButton.PRIMARY;
}

// Gives the X-Y pixel position of the mouse relative to the client area of the browser.
// This is useful if you need absolute coordinates that are not affected by the element the mouse is on.
// However it will give you results that are subtly (or not so subtly) off if you use it to get position
// in a system with multiple tiles or other complex layout.
// Origin is upper left, with +X to the right and +Y down.
export function getMousePositionOnClient(event: React.MouseEvent): { x: number; y: number };
export function getMousePositionOnClient(event: MouseEvent): { x: number; y: number };
export function getMousePositionOnClient(event: React.MouseEvent | MouseEvent): {
  x: number;
  y: number;
} {
  return {
    x: event.clientX,
    y: event.clientY,
  };
}

// Gives the X-Y pixel position of the mouse relative to the element it is on.
// Falls back to clientX/Y if no currentTarget.
// Origin is upper left, with +X to the right and +Y down.
export type PointerLikeEvent = React.MouseEvent | MouseEvent | React.PointerEvent | PointerEvent;

export function getMousePositionOnElement(event: PointerLikeEvent): {
  x: number;
  y: number;
} {
  // Handle case where currentTarget exists.
  if (event.currentTarget) {
    const rect = (event.currentTarget as Element).getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }

  // Fallback: assume top-left origin if no target.
  return {
    x: event.clientX,
    y: event.clientY,
  };
}

// Gives the normalized X-Y position of the mouse relative to the element it is on.
// Dimensions are optional, and if not provided, will be extracted from the event target.
// Origin is center of the element, with +X to the right and +Y up.
// If dimensions are not provided, they will be extracted from the event target.
export function getNormalizedMousePositionOnElement(
  event: PointerLikeEvent,
  dimension?: { width: number; height: number },
): { x: number; y: number } {
  // Satisfy linter by casting to specific type.
  const position =
    'nativeEvent' in event
      ? getMousePositionOnElement(event as React.MouseEvent)
      : getMousePositionOnElement(event as MouseEvent);

  // If dimensions are not provided, extract them from the event target
  const targetDimension =
    dimension ||
    ((): { width: number; height: number } => {
      const target = event.currentTarget || ('nativeEvent' in event ? event.target : null);
      if (!target) {
        throw new Error('Could not determine target element for mouse event');
      }
      const rect = (target as Element).getBoundingClientRect();
      return {
        width: rect.width,
        height: rect.height,
      };
    })();

  position.x = (position.x / targetDimension.width) * 2 - 1;
  position.y = -(position.y / targetDimension.height) * 2 + 1;
  return position;
}
